// Endpoint precedence for the in-world OGS-stream WebSocket:
//   ?ws=  →  ?port=  →  VITE_STREAM_WS_URL  →  <convorcher ws origin>/ogs-stream/
//
// The GPU server is an INTERNAL host, like kimodo and aos-a2f — the browser cannot
// reach it. Convorcher fronts it at `/ogs-stream/` (a WebSocket reverse proxy, see
// that repo's app/services/ogsstream), so the default derives from the same
// convorcher origin the chat WebSocket uses. Deriving rather than configuring is
// deliberate: `/api` and `/ws` naming different environments is what ENG-901 was,
// and a third endpoint with its own default would reintroduce it.
//
// `?ws=` remains for pointing at a stream server directly — a GPU box on the desk,
// with no convorcher in between. That is the only supported way to bypass the
// gateway, and it exists because the alternative during development is running
// convorcher too.
//
// `?api_key=` mirrors ConvorcherClient exactly: a localhost-dev affordance, because
// the `.avataros.xyz` cookie cannot reach a local convorcher (RFC 6265). In
// production the env var is unset, no query param is sent, and the upgrade
// authenticates with the avataros_session cookie — which is also why the gateway
// restricts the Origin for cookie upgrades.
//
// `character` is forced by the caller (the in-world menu picks it, not the URL) and
// appended so a multi-character server streams the right subject; ?bitrate= /
// ?vertstride= pass through when present.
//
// Location/env are read by default but injectable so the precedence can be
// unit-tested without touching the real window.location or import.meta.env.
import { resolveRuntimeWsUrl } from '../../lib/convorcherTargets.js'

/** `wss://host/ws` → `wss://host/ogs-stream/`. The gateway lives beside the chat
 *  WebSocket on the same origin, so the path is swapped rather than re-derived. */
export function deriveStreamBase(convorcherWsUrl) {
  const u = new URL(convorcherWsUrl)
  u.pathname = '/ogs-stream/'
  u.search = ''
  return u.toString()
}

export function resolveStreamWsUrl(character, {
  search = typeof location !== 'undefined' ? location.search : '',
  hostname = typeof location !== 'undefined' ? location.hostname : 'localhost',
  env = typeof import.meta !== 'undefined' ? import.meta.env : {},
  // Deployed builds derive the gateway from the PAGE origin (nginx proxies
  // /ogs-stream/ to ${CONVORCHER_URL} at runtime, beside /ws — ENG-1009), so
  // the stream can never name a different convorcher environment than chat.
  dev = typeof import.meta !== 'undefined' ? !!import.meta.env?.DEV : true,
  locationOrigin = typeof location !== 'undefined' ? location.origin : '',
} = {}) {
  const params = new URLSearchParams(search)
  const ENV_WS = (env.VITE_STREAM_WS_URL || '').trim()
  const direct = params.get('ws')
    || (params.get('port') ? `ws://${hostname}:${params.get('port')}` : '')
    || ENV_WS
  let url = direct || deriveStreamBase(resolveRuntimeWsUrl(env, { dev, locationOrigin }))

  const enc = [`character=${character}`]
  // Only when going through the gateway: a directly-addressed stream server has no
  // authentication of its own, and sending it a key would just put the secret in
  // its logs.
  if (!direct && env.VITE_CONVORCHER_API_KEY) {
    enc.unshift(`api_key=${encodeURIComponent(env.VITE_CONVORCHER_API_KEY)}`)
  }
  for (const k of ['bitrate', 'vertstride']) { const v = params.get(k); if (v) enc.push(`${k}=${v}`) }
  url += (url.includes('?') ? '&' : '?') + enc.join('&')
  return url
}
